import { FormulaEquation, FormulaRouteChain, FormulaPathStep } from '../types';
import { FORMULA_EQUATIONS } from '../data/formulas';
import { getDescriptorsForFormula } from './formulaDescriptors';

/**
 * Build a fully annotated path step with rearranged formula and descriptors
 */
export function createStep(eq: FormulaEquation, calculatedVarId: string): FormulaPathStep {
  const usingVars = eq.variables.filter((v) => v !== calculatedVarId);
  const rearranged = eq.rearrangedLatex?.[calculatedVarId] || eq.latex;
  const { primary, alternates } = getDescriptorsForFormula(eq);

  return {
    equation: eq,
    calculatedVariable: calculatedVarId,
    usingVariables: usingVars,
    rearrangedLatex: rearranged,
    descriptorCode: primary.code,
    descriptorTheme: primary.theme,
    descriptorDescription: primary.description,
    alternateDescriptors: alternates.length > 0 ? alternates : undefined,
  };
}

/**
 * Helper to expand variable aliases (e.g. t <-> deltat)
 */
export function expandKnownAliases(knowns: Set<string>): void {
  if (knowns.has('t')) knowns.add('deltat');
  if (knowns.has('deltat')) knowns.add('t');
}

interface QueueNode {
  knowns: Set<string>;
  steps: FormulaPathStep[];
  usedEqIds: Set<string>;
}

/**
 * Deterministic multi-step hyperedge BFS solver:
 * Explores equation graphs up to maxDepth without any recursion.
 */
export function findMinimalDeductionChains(
  initialKnowns: Set<string>,
  targetVarId: string,
  maxDepth = 4
): FormulaRouteChain[] {
  if (initialKnowns.has(targetVarId)) {
    return [];
  }

  // 1. Direct 1-step check: find ALL single formulas that connect knowns -> target
  const direct1StepChains: FormulaRouteChain[] = [];
  for (const eq of FORMULA_EQUATIONS) {
    if (eq.variables.includes(targetVarId)) {
      const remaining = eq.variables.filter((v) => v !== targetVarId && !initialKnowns.has(v));
      if (remaining.length === 0) {
        direct1StepChains.push({
          id: `chain-direct-${eq.id}`,
          steps: [createStep(eq, targetVarId)],
        });
      }
    }
  }

  if (direct1StepChains.length > 0) {
    return direct1StepChains;
  }

  if (maxDepth <= 1) {
    return [];
  }

  // 2. Multi-step BFS search over deducible variable states to find minimal chains
  let currentLevel: QueueNode[] = [
    {
      knowns: new Set(initialKnowns),
      steps: [],
      usedEqIds: new Set(),
    },
  ];

  const visitedSignatures = new Set<string>();
  const validMinimalChains: FormulaRouteChain[] = [];
  let foundMinimalDepth: number | null = null;
  let depth = 0;

  while (currentLevel.length > 0 && depth < maxDepth) {
    depth++;
    const nextLevel: QueueNode[] = [];

    for (const current of currentLevel) {
      const stateSig = Array.from(current.knowns).sort().join(',');
      if (visitedSignatures.has(stateSig)) continue;
      visitedSignatures.add(stateSig);

      for (const eq of FORMULA_EQUATIONS) {
        if (current.usedEqIds.has(eq.id)) continue;

        const unknowns = eq.variables.filter((v) => !current.knowns.has(v));
        if (unknowns.length === 1) {
          const deducedVar = unknowns[0];
          const nextKnowns = new Set(current.knowns);
          nextKnowns.add(deducedVar);
          expandKnownAliases(nextKnowns);

          const nextUsedEqIds = new Set(current.usedEqIds);
          nextUsedEqIds.add(eq.id);

          const step = createStep(eq, deducedVar);
          const nextSteps = [...current.steps, step];

          const isTargetReached =
            deducedVar === targetVarId ||
            (targetVarId === 't' && deducedVar === 'deltat') ||
            (targetVarId === 'deltat' && deducedVar === 't');

          if (isTargetReached) {
            if (foundMinimalDepth === null) {
              foundMinimalDepth = depth;
            }
            if (depth === foundMinimalDepth) {
              validMinimalChains.push({
                id: `chain-${validMinimalChains.length + 1}`,
                steps: nextSteps,
              });
            }
          } else if (foundMinimalDepth === null) {
            nextLevel.push({
              knowns: nextKnowns,
              steps: nextSteps,
              usedEqIds: nextUsedEqIds,
            });
          }
        }
      }
    }

    if (foundMinimalDepth !== null) {
      // Finished minimal depth level; do not explore deeper
      break;
    }

    currentLevel = nextLevel;
  }

  return validMinimalChains;
}
